import {Inject, Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {Subject} from 'rxjs';

import {APP_CONFIG} from '../app-config.constants';
import {IAppConfig} from '../app-config.interface';

import {ApiService, ApiResponse} from './api.service';
import {CredentialsService} from './credentials.service';
import {ErrorKey, ErrorService} from './error.service';
import {UserService} from './user.service';

import {TokenResponse, UserStatusFlag} from '../classes/token-response';
import {SocialType} from '../classes/enums';
import { map } from 'rxjs/operators';

export enum LoginResult {
  LoggedIn,
  InvalidCredentails,
  ServiceNotAvailable,
  InvalidResetToken,
  PasswordResetSuccessful,
  NewUserRequiresPasswordChange,
  TerminatedUser,
  EmailSent,
  UserNotFound,
  AccountsAvailable
}

@Injectable()
export class LoginService {
  public accountsAvailable: Subject<Array<SocialType>> = new Subject<Array<SocialType>>();

  constructor(@Inject(APP_CONFIG) private appConfig: IAppConfig
    , private _api: ApiService
    , private _credentialService: CredentialsService
    , private _userService: UserService
    , private _errorService: ErrorService) {
  }

  public login(email: string, password: string, rememberMe: boolean): Observable<LoginResult> {
    const body = {
      email: email,
      password: password,
      rememberme: rememberMe
    };

    const req =
      this._api.postRequest<TokenResponse>(this.appConfig.urls.user.login, body)
        .doesNotRequireTokenAuthentication()
        .build()
        .pipe(map(result => {
          return this.processLoginResult(result, rememberMe);
        }));
    return req;
  }

  public socialLogin(accessToken: string, provider: string, proceedWithSocial: boolean): Observable<LoginResult> {
    const body = {
      access_token: accessToken,
      provider: provider,
      proceed_with_social_login: proceedWithSocial
    };

    const req =
      this._api.postRequest<TokenResponse>(this.appConfig.urls.user.socialLogin, body)
        .doesNotRequireTokenAuthentication()
        .build()
        .pipe(map(result => {
          //Defaulting social login to rememberMe = true;
          return this.processLoginResult(result, true);
        }));
    return req;
  }

  private processLoginResult(result: ApiResponse<TokenResponse>, rememberMe: boolean): LoginResult {
    if (result.error) {
      if (result.error.errorCode === 42038) {
      } else if (result.error.errorCode === 42042) {
        return LoginResult.TerminatedUser;
      } else if (result.error.errorCode === 42007) {
        return LoginResult.UserNotFound;
      } else if (result.error.errorCode === 41048) {
        return LoginResult.NewUserRequiresPasswordChange;
      } else if (result.error.statusCode === 401) {
        return LoginResult.InvalidCredentails;
      } else if (result.error.statusCode === 404) {
        return LoginResult.UserNotFound;
      }
      return LoginResult.ServiceNotAvailable;
    }

    if (result.response && result.response.access_token) {
      // All is good, put their tokens into cookies and let everyone know the user is logged in.
      this._credentialService.setTokens(result.response.access_token, result.response.expires_in, rememberMe);
    } else if (result.response && result.response['accounts']) {
      this.accountsAvailable.next(result.response['accounts']);
      return LoginResult.AccountsAvailable;
    } else {
      return LoginResult.ServiceNotAvailable;
    }

    this._errorService.clearAllErrors();

    if (result.response.user_status_flag === UserStatusFlag.InactiveLimitedAccess) {
      this._errorService.displayError(ErrorKey.inactiveUser);
    }

    return LoginResult.LoggedIn;
  }

  public logout(): Observable<boolean> {
    const req =
      this._api.postRequest<{}>(this.appConfig.urls.user.logout, {})
        .doesNotRequireTokenAuthentication()
        .build()
        .pipe(map(result => {
          this._credentialService.removeTokens();
          this._userService.removeLocalUser();

          if (result.error) {
            // console.log("Error Result from log out: " + JSON.stringify(result.error));
          }

          return !result.error;
        }));
    return req;
  }

  public checkPasswordToken(token: string): Promise<boolean> {
    const req =
      this._api.getRequest<boolean>(this.appConfig.urls.user.checkPasswordToken + token)
        .doesNotRequireTokenAuthentication()
        .build()
        .pipe(map(result => {

          if (result.error) {
            // console.error("Error from password token", result.error);
            return false;
          }

          return result.response;
        }));
    return req.toPromise();
  }

  public changeForgotPassword(forgotPasswordToken: string, password: string): Promise<LoginResult> {
    const body = {
      new_password: password,
      reset_token: forgotPasswordToken
    };

    const req =
      this._api.postRequest<TokenResponse>(this.appConfig.urls.user.resetPassword, body)
        .doesNotRequireTokenAuthentication()
        .build()
        .pipe(map(result => {

          if (result.error) {
            // console.error("Error from reset password: " + JSON.stringify(result.error));
            if (result.error.statusCode === 401 || result.error.statusCode === 400 || result.error.statusCode === 404 || result.error.statusCode === 500) {
              return LoginResult.InvalidResetToken;
            }
            return LoginResult.ServiceNotAvailable;
          }

          return LoginResult.PasswordResetSuccessful;
        }));
    return req.toPromise();
  }

  public changePasswordWhileLoggedIn(password: string): Observable<LoginResult> {
    const body = {
      new_password: password
    };

    const req =
      this._api.postRequest<TokenResponse>(this.appConfig.urls.user.changePassword, body)
        .build()
        .pipe(map(result => {

          if (result.error) {
            // console.error("Error from reset password: " + JSON.stringify(result.error));
            return LoginResult.ServiceNotAvailable;
          }

          return LoginResult.PasswordResetSuccessful;
        }));
    return req;
  }

  public sendForgotPasswordEmail(username: string): Promise<LoginResult> {
    const body = {
      username: username
    };

    const req =
      this._api.postRequest<TokenResponse>(this.appConfig.urls.user.forgotPassword, body)
        .doesNotRequireTokenAuthentication()
        .build()
        .pipe(map(result => {

          if (result.error) {
            // console.error("Error from reset password: " + JSON.stringify(result.error));
            if (result.error.statusCode === 401 || result.error.statusCode === 400) {
              return LoginResult.InvalidCredentails;
            }
            return LoginResult.ServiceNotAvailable;
          }

          return LoginResult.EmailSent;
        }));
    return req.toPromise();
  }
}
